import { useMutation, useQueryClient } from "@tanstack/react-query"
import { deleteJobAction } from "../../actions/deleteJob.action"
import { useGlobalSnackbar } from "../../../../../context/SnackbarContext";


export const useDeleteJobAction = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useGlobalSnackbar();

    return useMutation({
        mutationFn: (jobId: string) => deleteJobAction(jobId),
        onMutate: async (jobId) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: ['jobs'] });

            // Snapshot the previous value
            const previousJobsQueries = queryClient.getQueriesData<any>({ queryKey: ['jobs'] });

            // Optimistically update to the new value
            queryClient.setQueriesData<any>({ queryKey: ['jobs'] }, (old: any) => {
                if (!old) return old;

                // Handle different response structures (direct array vs { data: { items: [] } })
                if (Array.isArray(old)) {
                    return old.filter((job) => job.id !== jobId);
                }

                if (old.data?.items) {
                    return {
                        ...old,
                        data: {
                            ...old.data,
                            items: old.data.items.filter((job: any) => job.id !== jobId)
                        }
                    };
                }

                return old;
            });

            // Return a context object with the snapshotted value
            return { previousJobsQueries };
        },
        onError: (err: any, jobId, context) => {
            // Rollback on error
            if (context?.previousJobsQueries) {
                context.previousJobsQueries.forEach(([queryKey, oldData]) => {
                    queryClient.setQueryData(queryKey, oldData);
                });
            }
            showSnackbar(err.message || "Failed to delete job", "error");
        },
        onSuccess: () => {
            showSnackbar("Job deleted successfully", "success");
        },
        onSettled: () => {
            // Always refetch after error or success to keep server in sync
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
        }
    })
}